import { useEffect, useState, FormEvent, ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { OrganizationSchema, WebsiteSchema, HomePageSchema, BreadcrumbSchema } from '@/components/feature/SchemaOrg';
import { statistics } from '@/mocks/homeData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9reb4vnahb7382ovlc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries()).map(([k, v]) => [k, v.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <OrganizationSchema />
      <WebsiteSchema />
      <HomePageSchema />
      <BreadcrumbSchema items={[
        { name: '首页', url: '/' },
        { name: '关于我们', url: '/about' },
      ]} />
      <Navbar />
      <main className="pt-16">
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 font-medium">关于我们</span>
              </nav>

              <div className="text-center mb-12 md:mb-16">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 mb-4">关于九州体育</h1>
                <p className="text-foreground-500 text-base md:text-lg max-w-[700px] mx-auto leading-relaxed">
                  九州体育是专业的综合体育服务平台，致力于为每一位体育爱好者提供极致体验
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center mb-14 md:mb-20">
                <div className="relative h-72 md:h-96 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20sports%20technology%20company%20office%20with%20sleek%20open%20workspace%20and%20large%20screens%20displaying%20live%20sports%20data%2C%20warm%20natural%20lighting%2C%20contemporary%20design%20with%20sports%20memorabilia%2C%20professional%20atmosphere%20with%20golden%20and%20warm%20tones&width=900&height=600&seq=about-brand&orientation=landscape"
                    alt="九州体育品牌"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">品牌介绍</h2>
                  <div className="space-y-4 text-foreground-600 leading-relaxed text-sm md:text-base">
                    <p>
                      <strong className="text-foreground-900">九州体育</strong>成立于2018年，总部位于北京，是国内领先的全场景体育服务平台。我们以&ldquo;让每一位体育爱好者都能享受极致的体育体验&rdquo;为使命，通过技术创新和优质服务，为数千万用户提供赛事直播、体育资讯、体育竞猜、运动健康等一站式服务。
                    </p>
                    <p>
                      九州体育平台集结了来自全球200+顶级体育赛事资源，涵盖足球、篮球、网球、电竞、赛车、搏击、游泳、高尔夫等多个热门体育项目。我们采用领先的流媒体技术，为用户提供高清无延迟的直播体验。
                    </p>
                    <p>
                      九州体育拥有一支由体育行业专家、资深数据分析师和专业内容创作团队组成的核心力量，为用户提供最及时、最专业、最有深度的体育内容和竞猜分析。
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-14 md:mb-20">
                {statistics.map((stat) => (
                  <div key={stat.label} className="text-center p-6 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-2">{stat.value}</div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="text-center mb-10 md:mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">用户口碑</h2>
                <p className="text-sm text-foreground-500">来自九州体育真实用户的评价</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;九州体育的直播画质真的非常好，延迟也很低。我用了很多体育平台，九州的体验是最好的。赛事覆盖也很全面，几乎所有我想看的比赛都有。&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-600 font-bold text-sm">张</div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">张先生</div>
                      <div className="text-xs text-foreground-400">资深足球迷，注册2年</div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;九州体育的竞猜分析非常专业，我跟着推荐投注的命中率明显提高了。而且提款速度真的快，基本上申请后30秒就到账了，比其他平台强太多了。&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm">李</div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">李先生</div>
                      <div className="text-xs text-foreground-400">竞猜爱好者，注册1年半</div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;九州体育的运动健康板块非常实用，里面的跑步热身教程和运动恢复建议对我的训练帮助很大。客服也非常专业耐心，有问题随时都能得到解答。&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-bold text-sm">王</div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">王女士</div>
                      <div className="text-xs text-foreground-400">健身达人，注册半年</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="text-center mb-10 md:mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">联系我们</h2>
                <p className="text-sm text-foreground-500">如有任何问题，欢迎随时与我们取得联系</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 max-w-[1000px] mx-auto">
                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-5 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-500">service@jiuzhousports.com</p>
                      <p className="text-xs text-foreground-400 mt-0.5">商务合作：business@jiuzhousports.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-5 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500 flex-shrink-0">
                      <i className="ri-phone-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                      <p className="text-sm text-foreground-500">400-888-9999</p>
                      <p className="text-xs text-foreground-400 mt-0.5">7×24小时全天候服务</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-5 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-500 flex-shrink-0">
                      <i className="ri-map-pin-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                      <p className="text-sm text-foreground-500">北京市朝阳区北辰西路8号</p>
                      <p className="text-xs text-foreground-400 mt-0.5">北辰世纪中心A座15层</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">在线咨询</h3>
                  <p className="text-sm text-foreground-500 mb-6 leading-relaxed">
                    填写以下信息，我们的客服团队将在24小时内与您联系。您也可以直接拨打客服热线获得即时帮助。
                  </p>
                  <form className="space-y-4" onSubmit={handleFormSubmit} data-readdy-form="">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-600 mb-1.5">姓名</label>
                      <input id="name" name="name" type="text" required className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" placeholder="请输入您的姓名" />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-600 mb-1.5">电子邮箱</label>
                      <input id="email" name="email" type="email" required className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors" placeholder="请输入您的邮箱" />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-xs font-medium text-foreground-600 mb-1.5">咨询内容</label>
                      <textarea id="message" name="message" rows={4} maxLength={500} required className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none" placeholder="请描述您的问题或需求（限500字）"></textarea>
                    </div>
                    <div className="sp-field-wrap">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button type="submit" disabled={formStatus === 'loading'} className="w-full py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60">
                      {formStatus === 'loading' ? '提交中...' : '提交咨询'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">感谢您的咨询，我们将尽快与您联系！</p>
                    )}
                    {formStatus === 'error' && formError && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}